import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Target, Brain, Sparkles, CheckCircle, Globe, Search, Building2, Shield, Wifi, FolderOpen, Loader2, Router, Lock, Cloud, AlertTriangle, EyeOff, Crown, Database, Key, LogIn, Eye } from 'lucide-react';
import { useI18n } from '../../i18n';
import CGUModal from '../CGUModal';
import SubdomainDiscoveryModal from '../SubdomainDiscoveryModal';
import VPNConnectionPanel from '../VPNConnectionPanel';
import FreePlanWarning from '../FreePlanWarning';
import ContractErrorModal from '../ContractErrorModal';
import { vpn as vpnApi } from '../../api/client';
import apiClient from '../../api/client';
import { getOnlineAgents } from '../../api/agents';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import useAuthStore from '../../store/authStore';
import { toast } from '../../utils/toastUtils';

// Normalize target: strip protocol (http://, https://) and trailing slashes/paths
const normalizeTarget = (target) => {
  if (!target) return '';
  let t = target.trim();
  // Remove protocol
  t = t.replace(/^https?:\/\//i, '');
  // Remove trailing slash and path (keep only host[:port])
  t = t.split('/')[0];
  // Remove trailing dot
  t = t.replace(/\.$/, '');
  return t;
};

// Function to detect if IP is public (not private)
const isPublicIP = (target) => {
  if (!target) return false;
  const trimmed = normalizeTarget(target);

  // Extract IP from CIDR if needed
  const ipPart = trimmed.split('/')[0];

  // Check if it's an IP address pattern
  const ipPattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  const match = ipPart.match(ipPattern);

  if (!match) {
    // Check if it's a domain - domains are considered public
    const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;
    return domainPattern.test(trimmed);
  }

  const [, octet1, octet2, octet3, octet4] = match.map(Number);

  // Validate IP octets
  if (octet1 > 255 || octet2 > 255 || octet3 > 255 || octet4 > 255) return false;

  // Private IP ranges (RFC 1918)
  // 10.0.0.0 - 10.255.255.255
  if (octet1 === 10) return false;

  // 172.16.0.0 - 172.31.255.255
  if (octet1 === 172 && octet2 >= 16 && octet2 <= 31) return false;

  // 192.168.0.0 - 192.168.255.255
  if (octet1 === 192 && octet2 === 168) return false;

  // Loopback 127.0.0.0 - 127.255.255.255
  if (octet1 === 127) return false;

  // Link-local 169.254.0.0 - 169.254.255.255
  if (octet1 === 169 && octet2 === 254) return false;

  // It's a public IP
  return true;
};

const AIScanWizard = ({ isOpen, onClose, onComplete }) => {
  // i18n
  const { t } = useI18n();
  // Get active workspace from context
  const { activeWorkspace, showSelector, setShowSelector } = useWorkspace();
  // Get user info for plan restrictions
  const { user } = useAuthStore();
  const isFreePlan = user?.simulation_only === true;

  // Always start at step 1 (Cible) — workspace is already selected
  const [currentStep, setCurrentStep] = useState(1);
  const [showCGUModal, setShowCGUModal] = useState(false);
  const [showSubdomainModal, setShowSubdomainModal] = useState(false);
  const [detectedDomain, setDetectedDomain] = useState(null);
  const [selectedSubdomains, setSelectedSubdomains] = useState([]);
  const [vpnStatus, setVpnStatus] = useState(null);
  const [vpnConfigs, setVpnConfigs] = useState({ interne: [], externe: [] });
  const [showPortalPassword, setShowPortalPassword] = useState(false);
  // Contract error modal state
  const [contractError, setContractError] = useState({
    isOpen: false,
    errorType: null,
    message: '',
    workspaceId: null,
    target: ''
  });
  // AI scan quota exceeded modal state
  const [aiQuotaError, setAiQuotaError] = useState({
    isOpen: false,
    message: '',
    used: 0,
    limit: 0
  });
  // Contract override options (for retrying scan with skip or specific contract)
  const [contractOverride, setContractOverride] = useState({
    skip_contract_check: false,
    contract_id: null
  });
  // Agent state for internal network scanning
  const [availableAgents, setAvailableAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '', // Optional scan name
    target: '',
    workspace_id: '', // Will be set from activeWorkspace
    agent_id: '', // Agent ID for internal network scanning via tunnel
    use_vpn: false, // Enable VPN for internal scan
    vpn_config_id: '', // Selected VPN config
    vpn_username: '',
    vpn_password: '',
    scan_type: 'full_autonomous',
    ai_analysis: true,
    ai_model: 'sylink',
    ai_assistance_mode: 'auto', // 'auto' or 'manual'
    enable_exploitation: false,
    severity_filter: ['critical', 'high'],
    analysis_depth: 'comprehensive',
    darkweb_search: false,
    test_darkweb_credentials: false,
    // Default tool settings for full_autonomous
    nmap_enabled: true,
    openvas_enabled: true,
    nuclei_enabled: true,
    nikto_enabled: false,
    sqlmap_enabled: false,
    gobuster_enabled: true,
    metasploit_enabled: true,
    zap_enabled: true,
    // Network routing mode for public targets
    network_mode: 'direct', // direct, vpn, proxy
    proxy_url: '', // e.g. socks5://host:port or http://host:8080
    // Stealth mode for scan evasion
    stealth_mode: 'none', // none, basic, advanced, paranoid
    // Public IP specific attack options
    public_ip_attacks: {
      enabled: false,
      box_firewall: false,
      vpn_server: false,
      default_creds: false,
      firmware_vuln: false,
      management_interface: false,
    },
    // Internal network specific attack options
    internal_network_attacks: {
      enabled: false,
      ad_enumeration: false,        // Active Directory enumeration
      smb_enumeration: false,       // SMB/CIFS shares discovery
      service_discovery: false,     // Internal services (DNS, DHCP, printers)
      lateral_movement: false,      // Lateral movement detection
      credential_spraying: false,   // Internal credential spraying
      network_poisoning: false,     // LLMNR/NBT-NS/mDNS poisoning
      kerberos_attacks: false,      // Kerberoasting, AS-REP roasting
      snmp_enumeration: false,      // SNMP community strings
    },
    // Client portal / web application attack options
    client_portal_attacks: {
      enabled: false,
      sql_injection: false,         // SQLMap on login forms
      credential_bruteforce: false, // Hydra / common credentials
      authenticated_scan: false,   // Test inside the portal with real credentials
      portal_email: '',
      portal_password: '',
    },
  });

  // Auto-set workspace_id and VPN from active workspace
  useEffect(() => {
    if (activeWorkspace?.id && !formData.workspace_id) {
      const updates = { workspace_id: activeWorkspace.id };

      // If workspace has a VPN configured, auto-enable it
      if (activeWorkspace.vpn_config_id) {
        updates.use_vpn = true;
        updates.vpn_config_id = activeWorkspace.vpn_config_id;
      }

      setFormData(prev => ({ ...prev, ...updates }));
    }
  }, [activeWorkspace?.id, activeWorkspace?.vpn_config_id]);

  // State for public IP detection
  const [isTargetPublic, setIsTargetPublic] = useState(false);

  // Fetch VPN configs when active workspace changes
  useEffect(() => {
    const fetchVpnConfigs = async () => {
      if (!isOpen) return;
      try {
        const response = await vpnApi.getConfigs(null, activeWorkspace?.id || null);
        setVpnConfigs({
          interne: response.interne || [],
          externe: response.externe || []
        });

        // If workspace has VPN and it's not yet set, auto-select it
        if (activeWorkspace?.vpn_config_id && !formData.vpn_config_id) {
          setFormData(prev => ({
            ...prev,
            use_vpn: true,
            vpn_config_id: activeWorkspace.vpn_config_id
          }));
        }
      } catch (error) {
        console.error('Failed to fetch VPN configs:', error);
        setVpnConfigs({ interne: [], externe: [] });
      }
    };
    fetchVpnConfigs();
  }, [isOpen, activeWorkspace?.id, activeWorkspace?.vpn_config_id]);

  // Fetch online agents when workspace changes
  // Strategy: prefer workspace-specific agents, fall back to company-wide agents
  useEffect(() => {
    const fetchAgents = async () => {
      if (!isOpen) return;
      setAgentsLoading(true);
      try {
        // First, try to get agents assigned to this workspace
        let agents = await getOnlineAgents(activeWorkspace?.id || null);
        // If no workspace-specific agents, fall back to ALL online agents for the company
        if (!agents || agents.length === 0) {
          agents = await getOnlineAgents(null);
        }
        setAvailableAgents(agents || []);
      } catch (error) {
        console.error('Failed to fetch agents:', error);
        setAvailableAgents([]);
      } finally {
        setAgentsLoading(false);
      }
    };
    fetchAgents();
  }, [isOpen, activeWorkspace?.id]);

  // Callback for VPN status changes from VPNSelector
  const handleVpnStatusChange = useCallback((status) => {
    setVpnStatus(status);
  }, []);

  // 4 steps — workspace is always pre-selected
  const steps = [
    { id: 1, title: 'Cible', icon: Target },
    { id: 2, title: 'Paramètres IA', icon: Brain },
    { id: 3, title: 'Avancée', icon: Sparkles },
    { id: 4, title: 'Lancement', icon: CheckCircle },
  ];

  // Function to detect if input is a domain (not IP or CIDR)
  const isDomain = (target) => {
    if (!target) return false;
    const trimmed = normalizeTarget(target);
    // IP address patterns
    const ipPattern = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}(\/\d{1,2})?$/;
    // CIDR pattern
    const cidrPattern = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2}$/;
    // Range pattern
    const rangePattern = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\s*-\s*\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;

    if (ipPattern.test(trimmed) || cidrPattern.test(trimmed) || rangePattern.test(trimmed)) {
      return false;
    }
    // Domain pattern: must contain at least one dot and valid characters
    const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;
    return domainPattern.test(trimmed);
  };

  // Effect to detect domain when target changes
  useEffect(() => {
    const normalized = normalizeTarget(formData.target);
    if (isDomain(normalized)) {
      setDetectedDomain(normalized);
    } else {
      setDetectedDomain(null);
      setSelectedSubdomains([]);
    }
  }, [formData.target]);

  // Effect to detect public IP + auto-configure VPN/agent
  useEffect(() => {
    const targetIsPublic = isPublicIP(formData.target);
    setIsTargetPublic(targetIsPublic);

    if (targetIsPublic) {
      // PUBLIC target → use Surfshark VPN (prefer France-Paris, then any with proper name)
      const surfsharkVpn = vpnConfigs.externe?.find(v =>
        v.provider === 'Surfshark' && v.country === 'France' && v.remote_host?.includes('fr-par')
      ) || vpnConfigs.externe?.find(v =>
        v.provider === 'Surfshark' && v.country === 'France'
      ) || vpnConfigs.externe?.find(v =>
        v.provider === 'Surfshark' && v.name && v.name !== 'None - None'
      ) || vpnConfigs.externe?.[0];
      setFormData(prev => ({
        ...prev,
        public_ip_attacks: { ...prev.public_ip_attacks, enabled: true },
        internal_network_attacks: { ...prev.internal_network_attacks, enabled: false },
        // Auto-select Surfshark VPN for public scans
        use_vpn: surfsharkVpn ? true : prev.use_vpn,
        vpn_config_id: surfsharkVpn ? surfsharkVpn.id : prev.vpn_config_id,
        agent_id: '', // No agent for public scans
        network_mode: surfsharkVpn ? 'vpn' : 'direct'
      }));
    } else if (formData.target) {
      // INTERNAL target → use agent (if available) or workspace VPN
      const bestAgent = availableAgents.length > 0 ? availableAgents[0] : null;
      const workspaceVpn = vpnConfigs.interne?.length > 0 ? vpnConfigs.interne[0] : null;

      setFormData(prev => ({
        ...prev,
        internal_network_attacks: { ...prev.internal_network_attacks, enabled: true },
        public_ip_attacks: { ...prev.public_ip_attacks, enabled: false },
        // Use agent if available, otherwise workspace VPN
        agent_id: bestAgent ? bestAgent.id : '',
        use_vpn: !bestAgent && workspaceVpn ? true : false,
        vpn_config_id: !bestAgent && workspaceVpn ? workspaceVpn.id : prev.vpn_config_id,
        network_mode: bestAgent ? 'agent' : (workspaceVpn ? 'vpn' : 'direct'),
      }));
    }
  }, [formData.target, vpnConfigs, availableAgents]);

  // Handler for subdomain selection
  const handleSubdomainsSelected = (data) => {
    setSelectedSubdomains(data.subdomains);
    // Store the attack configuration
    setFormData(prev => ({
      ...prev,
      subdomain_attack: {
        subdomains: data.subdomains,
        attackMode: data.attackMode,
        aiModel: data.aiModel
      }
    }));
  };

  // Plans requiring contract validation
  const plansRequiringContract = ['free', 'enterprise', 'entreprise', 'groupe', 'group'];
  const userPlan = user?.subscription?.plan?.toLowerCase() || 'free';
  const requiresContract = plansRequiringContract.includes(userPlan);

  // Validate contract before proceeding from Target step
  const validateContract = async () => {
    if (!requiresContract || !formData.workspace_id || !formData.target) {
      return true; // No validation needed
    }

    try {
      const response = await apiClient.post('/contracts/validate-for-scan', {
        workspace_id: formData.workspace_id,
        target: formData.target
      });

      // Check the validation result
      if (response.data.valid) {
        return true;
      }

      // Contract validation failed - show error modal
      const reason = response.data.reason;
      setContractError({
        isOpen: true,
        errorType: reason === 'target_not_in_perimeter' ? 'target_not_in_perimeter' : 'contract_required',
        message: response.data.message,
        workspaceId: formData.workspace_id,
        target: formData.target
      });
      return false;
    } catch (error) {
      // Network or server error - show error
      const detail = error.response?.data?.detail;
      setContractError({
        isOpen: true,
        errorType: 'contract_required',
        message: typeof detail === 'string' ? detail : (detail?.message || 'Erreur de validation du contrat.'),
        workspaceId: formData.workspace_id,
        target: formData.target
      });
      return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    // Validate contract before launching scan (at the last step)
    // Skip validation if user already chose to bypass contract check
    if (formData.target && formData.workspace_id && !contractOverride.skip_contract_check && !contractOverride.contract_id) {
      const isValid = await validateContract();
      if (!isValid) {
        return; // Stop - contract error modal will be shown
      }
    }
    // Afficher le modal CGU avant de lancer le scan
    setShowCGUModal(true);
  };

  const handleCGUAccept = async (overrideOptions = {}) => {
    try {
      // For free plan users, force simulation mode and disable advanced features
      let submitData = { ...formData };
      if (isFreePlan) {
        submitData = {
          ...submitData,
          simulation_mode: true,
          enable_exploitation: false,
          darkweb_search: false,
          test_darkweb_credentials: false,
          stealth_mode: 'none',
          // Disable advanced tools for free plan
          metasploit_enabled: false,
          sqlmap_enabled: false,
          nikto_enabled: false,
        };
      }

      // Apply contract override options
      if (overrideOptions.skip_contract_check || contractOverride.skip_contract_check) {
        submitData.skip_contract_check = true;
      }
      if (overrideOptions.contract_id || contractOverride.contract_id) {
        submitData.contract_id = overrideOptions.contract_id || contractOverride.contract_id;
      }

      await onComplete(submitData);
      onClose();
    } catch (error) {
      const detail = error.response?.data?.detail;

      // Check for AI scan quota exceeded error (403)
      if (error.response?.status === 403 && detail?.error === 'ai_scan_quota_exceeded') {
        setAiQuotaError({
          isOpen: true,
          message: detail.message,
          used: detail.used || 0,
          limit: detail.limit || 0
        });
      }
      // Check for contract validation errors (403)
      else if (error.response?.status === 403 && (detail?.error === 'contract_required' || detail?.error === 'target_not_in_perimeter')) {
        // Show contract error modal with guidance
        setContractError({
          isOpen: true,
          errorType: detail.error,
          message: detail.message,
          workspaceId: detail.workspace_id,
          target: formData.target
        });
      // Check for insufficient credits error (402)
      } else if (error.response?.status === 402) {
        if (detail?.error === 'insufficient_credits') {
          toast.error('Crédits insuffisants pour lancer ce scan. Passez à un plan supérieur.');
        } else {
          toast.error('Credits insuffisants. Veuillez acheter des credits pour continuer.');
        }
      } else {
        toast.error('Erreur lors de la creation du scan: ' + (typeof detail === 'object' ? detail.message || JSON.stringify(detail) : detail || error.message));
      }
    }
  };

  // Handle continue without contract — submit directly with skip_contract_check
  const handleContinueWithoutContract = () => {
    setContractError({ isOpen: false, errorType: null, message: '', workspaceId: null, target: '' });
    handleCGUAccept({ skip_contract_check: true });
  };

  // Handle contract selection — submit directly with selected contract
  const handleSelectContract = (contractId) => {
    setContractError({ isOpen: false, errorType: null, message: '', workspaceId: null, target: '' });
    handleCGUAccept({ contract_id: contractId });
  };

  if (!isOpen) return null;

  return (
    <>
    {/* Modal CGU */}
    <CGUModal
      isOpen={showCGUModal}
      onClose={() => setShowCGUModal(false)}
      onAccept={handleCGUAccept}
      scanType="Scan AI Automatisé"
    />

    {/* Modal Subdomain Discovery */}
    <SubdomainDiscoveryModal
      isOpen={showSubdomainModal}
      onClose={() => setShowSubdomainModal(false)}
      domain={detectedDomain}
      onSubdomainsSelected={handleSubdomainsSelected}
      aiModel={formData.ai_model}
      scanType={formData.scan_type}
    />

    {/* Modal Contract Error */}
    <ContractErrorModal
      isOpen={contractError.isOpen}
      onClose={() => {
        setContractError({ isOpen: false, errorType: null, message: '', workspaceId: null, target: '' });
      }}
      errorType={contractError.errorType}
      message={contractError.message}
      workspaceId={contractError.workspaceId}
      target={contractError.target}
      onContinueWithoutContract={handleContinueWithoutContract}
      onSelectContract={handleSelectContract}
    />

    {/* Modal AI Quota Exceeded */}
    {aiQuotaError.isOpen && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70">
        <div className="bg-brand-black-light border border-brand-gray-dark rounded-xl p-6 max-w-lg w-full mx-4 shadow-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-orange-500/20 rounded-full">
              <AlertTriangle className="w-6 h-6 text-orange-400" />
            </div>
            <h2 className="text-xl font-bold text-white">{t('scans.aiQuotaExceeded') || 'Quota de scans IA atteint'}</h2>
          </div>

          <p className="text-gray-300 mb-4">
            {aiQuotaError.message}
          </p>

          <div className="bg-brand-black p-4 rounded-lg mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400">{t('scans.aiScansUsed') || 'Scans IA utilisés'}</span>
              <span className="text-white font-bold">{aiQuotaError.used} / {aiQuotaError.limit}</span>
            </div>
            <div className="w-full bg-brand-gray-dark rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
            <p className="text-blue-400 text-sm">
              <strong>{t('scans.upgradeOptions') || 'Options pour continuer'}:</strong>
            </p>
            <ul className="text-gray-300 text-sm mt-2 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-blue-400">1.</span>
                {t('scans.callSylink') || 'Appelez le service client SYLink:'} <a href="tel:+33415540000" className="text-brand-green hover:underline font-semibold">+33 4 15 54 00 00</a>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400">2.</span>
                {t('scans.upgradePlan') || 'Passez à un abonnement supérieur pour des scans IA illimités'}
              </li>
            </ul>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setAiQuotaError({ isOpen: false, message: '', used: 0, limit: 0 })}
              className="flex-1 px-4 py-2 bg-brand-gray-dark hover:bg-brand-gray-light text-white rounded-lg transition-colors"
            >
              {t('common.close') || 'Fermer'}
            </button>
            <button
              onClick={() => {
                setAiQuotaError({ isOpen: false, message: '', used: 0, limit: 0 });
                onClose();
                window.location.href = 'https://sylink-pentest.com/pricing';
              }}
              className="flex-1 px-4 py-2 bg-brand-green hover:bg-brand-green-dark text-black font-semibold rounded-lg transition-colors"
            >
              {t('scans.viewPlans') || 'Voir les offres'}
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-brand-black-light border-2 border-brand-green/30 rounded-2xl max-w-7xl w-full my-4 flex flex-col max-h-[95vh]">
        {/* Header */}
        <div className="p-6 border-b border-brand-green/20">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold text-gradient-green">Assistant de Scan IA</h2>
              <p className="text-gray-400 mt-1">Configurez le scan autonome intelligent</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-brand-black rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-400 hover:text-brand-green" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {steps.map((step, idx) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all ${
                      currentStep >= step.id
                        ? 'border-brand-green bg-brand-green/20 text-brand-green'
                        : 'border-gray-700 text-gray-600'
                    }`}
                  >
                    <step.icon className="w-6 h-6" />
                  </div>
                  <span className={`text-xs mt-2 ${currentStep >= step.id ? 'text-brand-green' : 'text-gray-600'}`}>
                    {step.title}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.id ? 'bg-brand-green' : 'bg-gray-700'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Free Plan Warning - shown on all steps */}
          <FreePlanWarning compact className="mb-4" />

          {/* Step 1: Target Configuration */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {/* Scan Name (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">
                  Nom du scan (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Audit Trimestriel Q1 2025, Pentest Client XYZ..."
                  className="input-field w-full"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Donnez un nom personnalisé pour identifier facilement ce scan
                </p>
              </div>

              {/* Target */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">
                  Cible (IP, CIDR, ou nom d'hôte) *
                </label>
                <input
                  type="text"
                  value={formData.target}
                  onChange={(e) => {
                    const val = e.target.value;
                    // Auto-strip protocol if user pastes a full URL
                    const normalized = /^https?:\/\//i.test(val) ? normalizeTarget(val) : val;
                    setFormData({ ...formData, target: normalized });
                  }}
                  placeholder="192.168.1.0/24 ou exemple.com"
                  className="input-field w-full text-lg"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2">
                  Exemples : 192.168.1.1, 192.168.1.0/24, exemple.com, 10.0.0.0/8
                </p>

                {/* Domain Detection - Subdomain Discovery Button */}
                {detectedDomain && (
                  <div className="mt-4 p-4 bg-brand-green/10 border border-brand-green/30 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Globe className="w-5 h-5 text-brand-green" />
                        <div>
                          <p className="text-sm text-brand-green font-medium">Domaine détecté</p>
                          <p className="text-xs text-gray-400">
                            Découvrir les sous-domaines pour <span className="font-mono">{detectedDomain}</span>
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowSubdomainModal(true)}
                        className="px-4 py-2 bg-brand-green text-brand-black rounded-lg font-medium hover:bg-brand-green/90 transition-colors flex items-center gap-2 text-sm"
                      >
                        <Search className="w-4 h-4" />
                        Découvrir les sous-domaines
                      </button>
                    </div>

                    {/* Show selected subdomains count */}
                    {selectedSubdomains.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-brand-green/20">
                        <p className="text-xs text-gray-400">
                          <span className="text-brand-green font-bold">{selectedSubdomains.length}</span> sous-domaines sélectionnés pour l'attaque
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {selectedSubdomains.slice(0, 5).map((sub, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-brand-green/20 text-brand-green text-xs rounded">
                              {sub.subdomain}
                            </span>
                          ))}
                          {selectedSubdomains.length > 5 && (
                            <span className="px-2 py-0.5 bg-gray-700 text-gray-400 text-xs rounded">
                              +{selectedSubdomains.length - 5} autres
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Client Portal / Web Application Testing Block */}
                {(detectedDomain || isTargetPublic) && (
                  <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <div className="flex items-center gap-3 mb-4">
                      <LogIn className="w-5 h-5 text-purple-400" />
                      <div>
                        <p className="text-sm text-purple-400 font-medium">Espace Client / Portail Web</p>
                        <p className="text-xs text-gray-400">
                          Tester la sécurité du portail client et des formulaires d'authentification
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                        Tests d'authentification & Injections
                      </label>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {/* SQL Injection */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-purple-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.client_portal_attacks.sql_injection}
                            onChange={(e) => setFormData({
                              ...formData,
                              client_portal_attacks: {
                                ...formData.client_portal_attacks,
                                sql_injection: e.target.checked,
                                enabled: e.target.checked || formData.client_portal_attacks.credential_bruteforce || formData.client_portal_attacks.authenticated_scan
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-purple-500 focus:ring-purple-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Database className="w-4 h-4 text-purple-400" />
                              <span className="text-sm font-medium text-white">Injections SQL</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              SQLMap sur formulaires login, champs email/password, reset de mot de passe
                            </p>
                          </div>
                        </label>

                        {/* Credential Bruteforce */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-purple-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.client_portal_attacks.credential_bruteforce}
                            onChange={(e) => setFormData({
                              ...formData,
                              client_portal_attacks: {
                                ...formData.client_portal_attacks,
                                credential_bruteforce: e.target.checked,
                                enabled: formData.client_portal_attacks.sql_injection || e.target.checked || formData.client_portal_attacks.authenticated_scan
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-purple-500 focus:ring-purple-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Key className="w-4 h-4 text-yellow-400" />
                              <span className="text-sm font-medium text-white">Test de Credentials</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Hydra, identifiants communs, comptes par défaut, mots de passe faibles
                            </p>
                          </div>
                        </label>
                      </div>

                      {/* Authenticated scan */}
                      <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-purple-500/50 cursor-pointer transition-all mt-1">
                        <input
                          type="checkbox"
                          checked={formData.client_portal_attacks.authenticated_scan}
                          onChange={(e) => setFormData({
                            ...formData,
                            client_portal_attacks: {
                              ...formData.client_portal_attacks,
                              authenticated_scan: e.target.checked,
                              enabled: formData.client_portal_attacks.sql_injection || formData.client_portal_attacks.credential_bruteforce || e.target.checked
                            }
                          })}
                          className="mt-1 rounded border-gray-600 text-purple-500 focus:ring-purple-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-green-400" />
                            <span className="text-sm font-medium text-white">Scan Authentifié — Intérieur du portail</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Fournir des identifiants réels pour tester les pages accessibles après connexion (IDOR, XSS, accès non autorisé...)
                          </p>
                        </div>
                      </label>

                      {/* Credentials fields — shown when authenticated_scan is checked */}
                      {formData.client_portal_attacks.authenticated_scan && (
                        <div className="mt-2 p-4 bg-gray-800/60 rounded-lg border border-gray-600 space-y-3">
                          <p className="text-xs font-medium text-gray-300 uppercase tracking-wider">Identifiants de connexion au portail</p>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Email / Identifiant</label>
                            <input
                              type="email"
                              placeholder="client@example.com"
                              value={formData.client_portal_attacks.portal_email}
                              onChange={(e) => setFormData({
                                ...formData,
                                client_portal_attacks: {
                                  ...formData.client_portal_attacks,
                                  portal_email: e.target.value
                                }
                              })}
                              className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/60 focus:ring-1 focus:ring-purple-500/30"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">Mot de passe</label>
                            <div className="relative">
                              <input
                                type={showPortalPassword ? 'text' : 'password'}
                                placeholder="••••••••"
                                value={formData.client_portal_attacks.portal_password}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  client_portal_attacks: {
                                    ...formData.client_portal_attacks,
                                    portal_password: e.target.value
                                  }
                                })}
                                className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 pr-10 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/60 focus:ring-1 focus:ring-purple-500/30"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPortalPassword(!showPortalPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                              >
                                {showPortalPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">
                            Ces identifiants seront utilisés uniquement pour le scan. Ils ne sont pas stockés de façon permanente.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Selection count */}
                    <p className="text-xs text-gray-500 mt-3">
                      {[
                        formData.client_portal_attacks.sql_injection,
                        formData.client_portal_attacks.credential_bruteforce,
                        formData.client_portal_attacks.authenticated_scan
                      ].filter(Boolean).length} option(s) sélectionnée(s)
                    </p>
                  </div>
                )}

                {/* Public IP Detection - Box/Firewall/VPN Attack Options */}
                {isTargetPublic && (
                  <div className="mt-4 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <div className="flex items-center gap-3 mb-4">
                      <AlertTriangle className="w-5 h-5 text-orange-400" />
                      <div>
                        <p className="text-sm text-orange-400 font-medium">IP/Domaine Publique Détectée</p>
                        <p className="text-xs text-gray-400">
                          Cible exposée sur Internet - Options d'attaque spécifiques disponibles
                        </p>
                      </div>
                    </div>

                    {/* Attack Options */}
                    <div className="space-y-3">
                      <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                        Types d'attaques Box / Firewall / VPN
                      </label>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {/* Box/Firewall Scanner */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-orange-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.public_ip_attacks.box_firewall}
                            onChange={(e) => setFormData({
                              ...formData,
                              public_ip_attacks: {
                                ...formData.public_ip_attacks,
                                box_firewall: e.target.checked
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Router className="w-4 h-4 text-orange-400" />
                              <span className="text-sm font-medium text-white">Box / Firewall / Router</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Routify, RouterSploit, Nmap firewall scripts, CVE check
                            </p>
                          </div>
                        </label>

                        {/* VPN Server Scanner */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-orange-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.public_ip_attacks.vpn_server}
                            onChange={(e) => setFormData({
                              ...formData,
                              public_ip_attacks: {
                                ...formData.public_ip_attacks,
                                vpn_server: e.target.checked
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Lock className="w-4 h-4 text-blue-400" />
                              <span className="text-sm font-medium text-white">Serveur VPN</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              OpenVPN, IPSec, WireGuard, Fortinet, Palo Alto CVEs
                            </p>
                          </div>
                        </label>

                        {/* Default Credentials */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-orange-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.public_ip_attacks.default_creds}
                            onChange={(e) => setFormData({
                              ...formData,
                              public_ip_attacks: {
                                ...formData.public_ip_attacks,
                                default_creds: e.target.checked
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Shield className="w-4 h-4 text-red-400" />
                              <span className="text-sm font-medium text-white">Credentials par défaut</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Admin par défaut, comptes constructeur, SSH/Telnet
                            </p>
                          </div>
                        </label>

                        {/* Firmware Vulnerabilities */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-orange-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.public_ip_attacks.firmware_vuln}
                            onChange={(e) => setFormData({
                              ...formData,
                              public_ip_attacks: {
                                ...formData.public_ip_attacks,
                                firmware_vuln: e.target.checked
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Cloud className="w-4 h-4 text-purple-400" />
                              <span className="text-sm font-medium text-white">Vulnérabilités Firmware</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              CVE firmware, backdoors, buffer overflow, RCE
                            </p>
                          </div>
                        </label>

                        {/* Management Interface */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-orange-500/50 cursor-pointer transition-all col-span-full">
                          <input
                            type="checkbox"
                            checked={formData.public_ip_attacks.management_interface}
                            onChange={(e) => setFormData({
                              ...formData,
                              public_ip_attacks: {
                                ...formData.public_ip_attacks,
                                management_interface: e.target.checked
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-orange-500 focus:ring-orange-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-cyan-400" />
                              <span className="text-sm font-medium text-white">Interface de Management Web</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Scan des interfaces web admin exposées (HTTPS/HTTP), FortiGate, pfSense, OPNsense, Cisco ASDM
                            </p>
                          </div>
                        </label>
                      </div>

                      {/* Quick Select All */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                        <span className="text-xs text-gray-500">
                          {Object.values(formData.public_ip_attacks).filter(v => v === true).length - (formData.public_ip_attacks.enabled ? 1 : 0)} option(s) sélectionnée(s)
                        </span>
                        <button
                          type="button"
                          onClick={() => setFormData({
                            ...formData,
                            public_ip_attacks: {
                              enabled: true,
                              box_firewall: true,
                              vpn_server: true,
                              default_creds: true,
                              firmware_vuln: true,
                              management_interface: true,
                            }
                          })}
                          className="text-xs text-orange-400 hover:text-orange-300 font-medium"
                        >
                          Tout sélectionner
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Agent Selection - For internal network scanning via Windows Agent */}
                {!isTargetPublic && formData.target && !isDomain(formData.target) && (
                  <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <Shield className="w-5 h-5 text-purple-400" />
                      <div>
                        <p className="text-sm text-purple-400 font-medium">Scanner via Agent Windows</p>
                        <p className="text-xs text-gray-400">
                          Utilisez un agent installé sur le réseau interne pour atteindre cette cible
                        </p>
                      </div>
                    </div>

                    {agentsLoading ? (
                      <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Chargement des agents...
                      </div>
                    ) : availableAgents.length > 0 ? (
                      <div className="space-y-2">
                        <select
                          value={formData.agent_id}
                          onChange={(e) => {
                            const agentId = e.target.value;
                            setFormData(prev => ({
                              ...prev,
                              agent_id: agentId,
                              // When using agent, disable VPN (agent IS the tunnel)
                              use_vpn: agentId ? false : prev.use_vpn,
                              network_mode: agentId ? 'agent' : prev.network_mode
                            }));
                          }}
                          className="input-field w-full"
                        >
                          <option value="">Sans agent (VPN ou direct)</option>
                          {availableAgents.map(agent => (
                            <option key={agent.id} value={agent.id}>
                              {agent.name || agent.hostname} — {agent.internal_ip || 'IP inconnue'} ({agent.status})
                            </option>
                          ))}
                        </select>
                        {formData.agent_id && (
                          <p className="text-xs text-purple-300">
                            Le scan sera routé via le tunnel WebSocket de l'agent vers {formData.target}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 py-2">
                        Aucun agent en ligne. <span className="text-purple-400">Installez un agent</span> sur le réseau cible ou utilisez un VPN.
                      </div>
                    )}
                  </div>
                )}

                {/* Internal Network Detection - Internal Network Attack Options */}
                {!isTargetPublic && formData.target && !isDomain(formData.target) && (
                  <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-center gap-3 mb-4">
                      <Wifi className="w-5 h-5 text-blue-400" />
                      <div>
                        <p className="text-sm text-blue-400 font-medium">Réseau Interne Détecté</p>
                        <p className="text-xs text-gray-400">
                          Cible sur réseau privé - Options d'attaque réseau interne disponibles
                        </p>
                      </div>
                    </div>

                    {/* Internal Attack Options */}
                    <div className="space-y-3">
                      <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                        Options d'attaque Réseau Interne
                      </label>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {/* Active Directory Enumeration */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-blue-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.internal_network_attacks.ad_enumeration}
                            onChange={(e) => setFormData({
                              ...formData,
                              internal_network_attacks: {
                                ...formData.internal_network_attacks,
                                ad_enumeration: e.target.checked
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-blue-400" />
                              <span className="text-sm font-medium text-white">Active Directory</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Enumération AD, LDAP, utilisateurs, groupes, GPO
                            </p>
                          </div>
                        </label>

                        {/* SMB/CIFS Enumeration */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-blue-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.internal_network_attacks.smb_enumeration}
                            onChange={(e) => setFormData({
                              ...formData,
                              internal_network_attacks: {
                                ...formData.internal_network_attacks,
                                smb_enumeration: e.target.checked
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <FolderOpen className="w-4 h-4 text-yellow-400" />
                              <span className="text-sm font-medium text-white">SMB / Partages</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Partages réseau, permissions, fichiers sensibles
                            </p>
                          </div>
                        </label>

                        {/* Service Discovery */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-blue-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.internal_network_attacks.service_discovery}
                            onChange={(e) => setFormData({
                              ...formData,
                              internal_network_attacks: {
                                ...formData.internal_network_attacks,
                                service_discovery: e.target.checked
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Search className="w-4 h-4 text-green-400" />
                              <span className="text-sm font-medium text-white">Services Internes</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              DNS, DHCP, imprimantes, bases de données
                            </p>
                          </div>
                        </label>

                        {/* Kerberos Attacks */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-blue-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.internal_network_attacks.kerberos_attacks}
                            onChange={(e) => setFormData({
                              ...formData,
                              internal_network_attacks: {
                                ...formData.internal_network_attacks,
                                kerberos_attacks: e.target.checked
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Lock className="w-4 h-4 text-red-400" />
                              <span className="text-sm font-medium text-white">Attaques Kerberos</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Kerberoasting, AS-REP Roasting, Golden/Silver Ticket
                            </p>
                          </div>
                        </label>

                        {/* Lateral Movement */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-blue-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.internal_network_attacks.lateral_movement}
                            onChange={(e) => setFormData({
                              ...formData,
                              internal_network_attacks: {
                                ...formData.internal_network_attacks,
                                lateral_movement: e.target.checked
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Router className="w-4 h-4 text-purple-400" />
                              <span className="text-sm font-medium text-white">Mouvement Latéral</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              PsExec, WMI, WinRM, Pass-the-Hash, Pass-the-Ticket
                            </p>
                          </div>
                        </label>

                        {/* Credential Spraying */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-blue-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.internal_network_attacks.credential_spraying}
                            onChange={(e) => setFormData({
                              ...formData,
                              internal_network_attacks: {
                                ...formData.internal_network_attacks,
                                credential_spraying: e.target.checked
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Shield className="w-4 h-4 text-orange-400" />
                              <span className="text-sm font-medium text-white">Pulvérisation de mots de passe</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Test de mots de passe courants sur comptes AD
                            </p>
                          </div>
                        </label>

                        {/* Network Poisoning */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-blue-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.internal_network_attacks.network_poisoning}
                            onChange={(e) => setFormData({
                              ...formData,
                              internal_network_attacks: {
                                ...formData.internal_network_attacks,
                                network_poisoning: e.target.checked
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-red-400" />
                              <span className="text-sm font-medium text-white">Empoisonnement Réseau</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              LLMNR, NBT-NS, mDNS, détection ARP spoofing
                            </p>
                          </div>
                        </label>

                        {/* SNMP Enumeration */}
                        <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-700 hover:border-blue-500/50 cursor-pointer transition-all">
                          <input
                            type="checkbox"
                            checked={formData.internal_network_attacks.snmp_enumeration}
                            onChange={(e) => setFormData({
                              ...formData,
                              internal_network_attacks: {
                                ...formData.internal_network_attacks,
                                snmp_enumeration: e.target.checked
                              }
                            })}
                            className="mt-1 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-cyan-400" />
                              <span className="text-sm font-medium text-white">Énumération SNMP</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Community strings, configuration équipements
                            </p>
                          </div>
                        </label>
                      </div>

                      {/* Quick Select All */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                        <span className="text-xs text-gray-500">
                          {Object.values(formData.internal_network_attacks).filter(v => v === true).length - (formData.internal_network_attacks.enabled ? 1 : 0)} option(s) sélectionnée(s)
                        </span>
                        <button
                          type="button"
                          onClick={() => setFormData({
                            ...formData,
                            internal_network_attacks: {
                              enabled: true,
                              ad_enumeration: true,
                              smb_enumeration: true,
                              service_discovery: true,
                              lateral_movement: true,
                              credential_spraying: true,
                              network_poisoning: true,
                              kerberos_attacks: true,
                              snmp_enumeration: true,
                            }
                          })}
                          className="text-xs text-blue-400 hover:text-blue-300 font-medium"
                        >
                          Tout sélectionner
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">
                  Type de scan
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    { value: 'network_discovery', title: t('scans.networkDiscovery', 'Découverte Réseau'), desc: t('scans.networkDiscoveryDesc', 'Découverte rapide des hôtes et ports') },
                    { value: 'vulnerability_scan', title: t('scans.vulnerabilityScan', 'Scan de Vulnérabilités'), desc: t('scans.vulnerabilityScanDesc', 'Analyse approfondie des vulnérabilités avec OpenVAS') },
                    { value: 'full_autonomous', title: t('scans.fullAutonomous', 'Autonome Complet'), desc: t('scans.fullAutonomousDesc', 'Évaluation complète avec analyse IA') }
                  ].map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, scan_type: type.value })}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        formData.scan_type === type.value
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-gray-800 hover:border-brand-green/50'
                      }`}
                    >
                      <div className="font-semibold text-white mb-1">{type.title}</div>
                      <div className="text-xs text-gray-500 break-words whitespace-normal">{type.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Network mode indicator */}
              <div className="bg-brand-black/50 rounded-lg p-4 border border-gray-700">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Mode réseau :</h4>
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-brand-green" />
                    <span className="text-sm text-white">
                      {activeWorkspace?.company_name || activeWorkspace?.name || 'Workspace'}
                    </span>
                  </div>
                  {formData.agent_id ? (
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-purple-400" />
                      <span className="text-sm text-purple-400">
                        Agent : {availableAgents.find(a => a.id === formData.agent_id)?.name || 'Agent'}
                      </span>
                    </div>
                  ) : formData.use_vpn ? (
                    <div className="flex items-center gap-2">
                      <Wifi className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-blue-400">
                        VPN : {[...vpnConfigs.interne, ...vpnConfigs.externe].find(v => v.id === formData.vpn_config_id)?.name || 'VPN auto'}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-400">Scan direct</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: AI Settings */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">
                  Sélection du modèle IA
                </label>
                {isFreePlan ? (
                  <>
                    <div className="relative">
                      <select
                        value="sylink"
                        disabled
                        className="input-field w-full opacity-70 cursor-not-allowed"
                      >
                        <option value="sylink">SYLink AI (Recommandé - Optimisé pour la sécurité)</option>
                      </select>
                      <Lock className="absolute right-10 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    </div>
                    <div className="flex items-center gap-2 mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <Crown className="w-4 h-4 text-amber-500" />
                      <span className="text-xs text-amber-400">
                        Plan Free: seul SYLink AI est disponible. Passez au Pro pour accéder aux autres modèles.
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <select
                      value={formData.ai_model}
                      onChange={(e) => setFormData({ ...formData, ai_model: e.target.value })}
                      className="input-field w-full"
                    >
                      <option value="sylink">SYLink AI (Recommandé - Optimisé pour la sécurité)</option>
                      <option value="sylink-thinking">SYLink Thinking 32B (Analyse approfondie)</option>
                      <option value="deepseek-r1">SYLink AI Avancé (Raisonnement avancé)</option>
                      <option value="qwen3">SYLink AI Rapide (Analyse rapide)</option>
                      <option value="mistral">SYLink AI Léger</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-2">
                      Choisissez le modèle IA pour l'analyse des vulnérabilités et les recommandations d'exploitation
                    </p>
                  </>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">
                  Mode d'assistance IA
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, ai_assistance_mode: 'auto' })}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      formData.ai_assistance_mode === 'auto'
                        ? 'border-brand-green bg-brand-green/10'
                        : 'border-gray-800 hover:border-brand-green/50'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg ${formData.ai_assistance_mode === 'auto' ? 'bg-brand-green/20' : 'bg-gray-800'}`}>
                        <Brain className="w-5 h-5 text-brand-green" />
                      </div>
                      <div className="font-semibold text-white">Auto</div>
                    </div>
                    <div className="text-xs text-gray-500 text-left">
                      L'IA analyse automatiquement les resultats et prend des decisions en temps reel. Intervention minimale requise.
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, ai_assistance_mode: 'manual' })}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      formData.ai_assistance_mode === 'manual'
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-gray-800 hover:border-blue-500/50'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg ${formData.ai_assistance_mode === 'manual' ? 'bg-blue-500/20' : 'bg-gray-800'}`}>
                        <Sparkles className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="font-semibold text-white">Manuel</div>
                    </div>
                    <div className="text-xs text-gray-500 text-left">
                      L'IA fournit des suggestions et analyses mais vous gardez le controle total. Validation requise a chaque etape.
                    </div>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">
                  Profondeur d'analyse
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'quick', title: 'Rapide', desc: 'Analyse basique' },
                    { value: 'standard', title: 'Standard', desc: 'Profondeur équilibrée' },
                    { value: 'comprehensive', title: 'Complète', desc: 'Analyse approfondie' }
                  ].map((depth) => (
                    <button
                      key={depth.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, analysis_depth: depth.value })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.analysis_depth === depth.value
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-gray-800 hover:border-brand-green/50'
                      }`}
                    >
                      <div className="font-semibold text-white text-sm">{depth.title}</div>
                      <div className="text-xs text-gray-500 mt-1">{depth.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-brand-black rounded-lg p-4 border border-brand-green/20">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="ai_analysis"
                    checked={formData.ai_analysis}
                    onChange={(e) => setFormData({ ...formData, ai_analysis: e.target.checked })}
                    className="w-5 h-5 accent-brand-green"
                  />
                  <label htmlFor="ai_analysis" className="text-sm text-gray-300 cursor-pointer">
                    <div className="font-medium">Activer l'analyse IA</div>
                    <div className="text-xs text-gray-500">Utiliser l'IA pour l'évaluation des vulnérabilités et les recommandations d'exploitation</div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Advanced Options */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-in fade-in duration-300">

              {/* Network Routing — public targets only */}
              {isTargetPublic && (
                <div className="bg-brand-black rounded-lg p-4 border border-brand-green/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Router className="w-4 h-4 text-brand-green" />
                    <span className="text-sm font-semibold text-gray-300">Routage Réseau</span>
                    <span className="ml-auto text-xs px-2 py-0.5 bg-brand-green/20 text-brand-green rounded-full">Internet / IP Publique</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Choisissez comment le trafic de scan sera acheminé vers la cible.</p>

                  <div className="grid grid-cols-3 gap-3">
                    {/* Direct */}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, network_mode: 'direct', use_vpn: false }))}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        formData.network_mode === 'direct'
                          ? 'border-gray-400 bg-gray-500/10'
                          : 'border-gray-800 hover:border-gray-600'
                      }`}
                    >
                      <Globe className="w-5 h-5 text-gray-400 mb-2" />
                      <div className="font-semibold text-white text-sm">Direct</div>
                      <div className="text-xs text-gray-500 mt-0.5">IP serveur, sans tunnel</div>
                    </button>

                    {/* VPN */}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        network_mode: 'vpn',
                        use_vpn: true,
                        vpn_config_id: prev.vpn_config_id || vpnConfigs.externe?.[0]?.id || '',
                      }))}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        formData.network_mode === 'vpn'
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-gray-800 hover:border-brand-green/50'
                      }`}
                    >
                      <Lock className="w-5 h-5 text-brand-green mb-2" />
                      <div className="font-semibold text-white text-sm">VPN</div>
                      <div className="text-xs text-gray-500 mt-0.5">Tunnel chiffré anonyme</div>
                    </button>

                    {/* Proxy */}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, network_mode: 'proxy', use_vpn: false }))}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        formData.network_mode === 'proxy'
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-gray-800 hover:border-blue-500/50'
                      }`}
                    >
                      <Router className="w-5 h-5 text-blue-400 mb-2" />
                      <div className="font-semibold text-white text-sm">Proxy</div>
                      <div className="text-xs text-gray-500 mt-0.5">HTTP / SOCKS5</div>
                    </button>
                  </div>

                  {/* VPN config selector */}
                  {formData.network_mode === 'vpn' && (
                    <div className="mt-3">
                      {vpnConfigs.externe?.length > 0 ? (
                        <select
                          value={formData.vpn_config_id}
                          onChange={(e) => setFormData(prev => ({ ...prev, vpn_config_id: e.target.value }))}
                          className="w-full px-3 py-2 bg-brand-black-light border border-brand-green/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/50"
                        >
                          <option value="">-- Sélectionner un VPN --</option>
                          {vpnConfigs.externe.map(v => (
                            <option key={v.id} value={v.id}>
                              {v.name || `${v.provider} — ${v.country || ''}`}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                          Aucun VPN externe configuré. Ajoutez-en un dans les Paramètres → VPN.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Proxy URL input */}
                  {formData.network_mode === 'proxy' && (
                    <div className="mt-3 space-y-2">
                      <input
                        type="text"
                        value={formData.proxy_url}
                        onChange={(e) => setFormData(prev => ({ ...prev, proxy_url: e.target.value }))}
                        placeholder="socks5://127.0.0.1:1080 ou http://host:8080"
                        className="w-full px-3 py-2 bg-brand-black-light border border-blue-500/30 rounded-lg text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      />
                      <p className="text-xs text-gray-500">
                        Formats acceptés : <code className="text-blue-400">socks5://host:port</code>, <code className="text-blue-400">http://host:port</code>, <code className="text-blue-400">http://user:pass@host:port</code>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Free Plan Restriction Banner */}
              {isFreePlan && (
                <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-2 border-amber-500/50 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-500/20 rounded-lg">
                      <Lock className="w-5 h-5 text-amber-500" />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-amber-400 flex items-center gap-2">
                        <Crown className="w-4 h-4" />
                        Mode Simulation - Plan Gratuit
                      </div>
                      <p className="text-sm text-gray-300 mt-1">
                        Les options avancees (exploitation, recherche dark web, mode stealth) sont reservees aux plans payants.
                        Votre scan s'executera en <span className="text-amber-400 font-semibold">mode simulation</span> avec des donnees de demonstration.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {!isFreePlan && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="text-yellow-500 mt-0.5">⚠️</div>
                    <div className="flex-1">
                      <div className="font-semibold text-yellow-500 mb-1">Avertissement Exploitation</div>
                      <div className="text-xs text-gray-400">
                        L'activation de l'exploitation tentera d'exploiter activement les vulnérabilités découvertes. À utiliser uniquement avec autorisation appropriée.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={`bg-brand-black rounded-lg p-4 border ${isFreePlan ? 'border-gray-700 opacity-60' : 'border-brand-green/20'}`}>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="enable_exploitation"
                    checked={isFreePlan ? false : formData.enable_exploitation}
                    onChange={(e) => !isFreePlan && setFormData({ ...formData, enable_exploitation: e.target.checked })}
                    disabled={isFreePlan}
                    className="w-5 h-5 accent-brand-green disabled:opacity-50"
                  />
                  <label htmlFor="enable_exploitation" className={`text-sm cursor-pointer ${isFreePlan ? 'text-gray-500' : 'text-gray-300'}`}>
                    <div className="font-medium flex items-center gap-2">
                      Activer l'exploitation automatique
                      {isFreePlan && <Lock className="w-3 h-3 text-gray-500" />}
                    </div>
                    <div className="text-xs text-gray-500">Tenter d'exploiter les vulnérabilités critiques et élevées</div>
                  </label>
                </div>
              </div>

              {/* Dark Web OSINT - Only for public IPs/domains */}
              {isTargetPublic && (
                <div className={`bg-brand-black rounded-lg p-4 border ${isFreePlan ? 'border-gray-700 opacity-60' : 'border-purple-500/20'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="checkbox"
                      id="darkweb_search"
                      checked={isFreePlan ? false : formData.darkweb_search}
                      onChange={(e) => !isFreePlan && setFormData({ ...formData, darkweb_search: e.target.checked })}
                      disabled={isFreePlan}
                      className="w-5 h-5 accent-purple-500 disabled:opacity-50"
                    />
                    <label htmlFor="darkweb_search" className={`text-sm cursor-pointer flex-1 ${isFreePlan ? 'text-gray-500' : 'text-gray-300'}`}>
                      <div className="font-medium flex items-center gap-2">
                        🕵️ Recherche Dark Web OSINT
                        <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">OSINT</span>
                        {isFreePlan && <Lock className="w-3 h-3 text-gray-500" />}
                      </div>
                      <div className="text-xs text-gray-500">Rechercher les comptes compromis sur le dark web pour ce domaine/IP</div>
                    </label>
                  </div>

                  {formData.darkweb_search && (
                    <div className="ml-8 mt-3 space-y-3">
                      {/* Champ domaine email */}
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-2">
                          Domaine Email Corporatif (optionnel)
                        </label>
                        <input
                          type="text"
                          value={formData.corporate_domain || ''}
                          onChange={(e) => setFormData({ ...formData, corporate_domain: e.target.value })}
                          placeholder="@exemple.fr ou exemple.fr"
                          className="w-full px-3 py-2 bg-brand-black border border-purple-500/50 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Recherche spécifique pour emails de ce domaine (ex: @kartoo.com)
                        </p>
                      </div>

                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="test_darkweb_credentials"
                          checked={formData.test_darkweb_credentials}
                          onChange={(e) => setFormData({ ...formData, test_darkweb_credentials: e.target.checked })}
                          className="w-4 h-4 accent-brand-green"
                        />
                        <label htmlFor="test_darkweb_credentials" className="text-xs text-gray-400 cursor-pointer">
                          Tester automatiquement les credentials trouvés sur les services découverts
                        </label>
                      </div>
                      <div className="bg-purple-900/20 border border-purple-500/20 rounded p-3 text-xs text-gray-400">
                        <p className="text-purple-400 font-semibold mb-1">ℹ️ Fonctionnement:</p>
                        <ul className="space-y-1 ml-4 list-disc">
                          <li>Recherche emails/usernames sur Dark Web</li>
                          <li>Génération de variations de mots de passe</li>
                          <li>Test automatique sur SSH, RDP, SMB, MySQL, etc.</li>
                          <li>Enregistrement des credentials valides en BDD</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Internal Network Advanced Options - Only for internal IPs */}
              {!isTargetPublic && formData.target && !isDomain(formData.target) && (
                <div className="bg-brand-black rounded-lg p-4 border border-blue-500/20">
                  <div className="flex items-center gap-3 mb-3">
                    <Wifi className="w-5 h-5 text-blue-400" />
                    <div className="flex-1">
                      <div className="font-medium text-blue-400 flex items-center gap-2">
                        🏢 Options Avancées Réseau Interne
                        <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">LAN</span>
                      </div>
                      <div className="text-xs text-gray-500">Options spécifiques aux réseaux internes et domaines Windows</div>
                    </div>
                  </div>

                  <div className="space-y-3 mt-4">
                    <div className="bg-blue-900/20 border border-blue-500/20 rounded p-3 text-xs text-gray-400">
                      <p className="text-blue-400 font-semibold mb-1">ℹ️ Options Réseau Interne activées:</p>
                      <ul className="space-y-1 ml-4 list-disc">
                        {formData.internal_network_attacks.ad_enumeration && <li>Active Directory: LDAP, utilisateurs, groupes, GPO</li>}
                        {formData.internal_network_attacks.smb_enumeration && <li>SMB: Partages réseau, permissions, fichiers sensibles</li>}
                        {formData.internal_network_attacks.service_discovery && <li>Services: DNS, DHCP, imprimantes, bases de données</li>}
                        {formData.internal_network_attacks.kerberos_attacks && <li>Kerberos: Kerberoasting, AS-REP Roasting</li>}
                        {formData.internal_network_attacks.lateral_movement && <li>Mouvement latéral: PsExec, WMI, WinRM, PTH</li>}
                        {formData.internal_network_attacks.credential_spraying && <li>Password Spraying sur comptes AD</li>}
                        {formData.internal_network_attacks.network_poisoning && <li>Poisoning: LLMNR, NBT-NS, mDNS</li>}
                        {formData.internal_network_attacks.snmp_enumeration && <li>SNMP: Community strings, configuration</li>}
                        {!Object.entries(formData.internal_network_attacks).some(([k, v]) => k !== 'enabled' && v) && (
                          <li className="text-gray-500">Aucune option sélectionnée - configurez dans l'étape Cible</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Stealth Mode Selector */}
              <div className="bg-brand-black rounded-lg p-4 border border-brand-green/20">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-400 mb-3">
                  <EyeOff className="w-4 h-4 text-brand-green" />
                  Mode Furtif
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'none', label: 'Aucun', desc: 'Scan rapide, détectable', icon: '⚡' },
                    { value: 'basic', label: 'Basique', desc: 'Paquets fragmentés', icon: '🔒' },
                    { value: 'advanced', label: 'Avancé', desc: 'Timing lent + spoofing', icon: '🛡️' },
                    { value: 'paranoid', label: 'Paranoïaque', desc: 'Évasion max (très lent)', icon: '👻' },
                  ].map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, stealth_mode: mode.value })}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        formData.stealth_mode === mode.value
                          ? 'border-brand-green bg-brand-green/10'
                          : 'border-gray-800 hover:border-brand-green/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{mode.icon}</span>
                        <span className="font-semibold text-white text-sm">{mode.label}</span>
                      </div>
                      <div className="text-xs text-gray-500">{mode.desc}</div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Les modes furtifs utilisent des techniques d'évasion pour éviter la détection IDS/IPS
                </p>
              </div>

              {formData.enable_exploitation && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-3">
                    Filtre de sévérité
                  </label>
                  <div className="space-y-2">
                    {['critical', 'high', 'medium', 'low'].map((severity) => (
                      <div key={severity} className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id={`severity-${severity}`}
                          checked={formData.severity_filter.includes(severity)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                severity_filter: [...formData.severity_filter, severity]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                severity_filter: formData.severity_filter.filter(s => s !== severity)
                              });
                            }
                          }}
                          className="w-4 h-4 accent-brand-green"
                        />
                        <label htmlFor={`severity-${severity}`} className="text-sm text-gray-300 capitalize cursor-pointer">
                          {severity}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Review & Launch */}
          {currentStep === 4 && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="bg-brand-black rounded-lg p-6 border border-brand-green/20">
                <h3 className="text-xl font-bold text-brand-green mb-4">Résumé de la configuration du scan</h3>

                <div className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500 block mb-1">Cible</span>
                      <span className="text-white font-mono">{formData.target}</span>
                      {isTargetPublic && (
                        <span className="ml-2 text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded">IP Publique</span>
                      )}
                      {!isTargetPublic && formData.target && !isDomain(formData.target) && (
                        <span className="ml-2 text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">Réseau Interne</span>
                      )}
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block mb-1">Client / Workspace</span>
                      <span className="text-white">
                        {activeWorkspace
                          ? (activeWorkspace.company_name || activeWorkspace.name || 'Client selectionne')
                          : <span className="text-gray-500 italic">Non specifie</span>}
                      </span>
                    </div>
                    {formData.name && (
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Nom du scan</span>
                        <span className="text-white">{formData.name}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-xs text-gray-500 block mb-1">Type de scan</span>
                      <span className="text-white capitalize">{formData.scan_type.replace(/_/g, ' ')}</span>
                    </div>
                  </div>

                  {/* Network Configuration */}
                  <div className="pt-3 border-t border-gray-700">
                    <span className="text-xs text-gray-500 block mb-2 font-semibold">Configuration Reseau</span>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Mode Réseau</span>
                        {formData.agent_id ? (
                          <span className="text-purple-400">
                            Agent: {availableAgents.find(a => a.id === formData.agent_id)?.name || 'Agent sélectionné'}
                          </span>
                        ) : formData.use_vpn ? (
                          <span className="text-blue-400">
                            {[...vpnConfigs.interne, ...vpnConfigs.externe].find(v => v.id === formData.vpn_config_id)?.name || 'VPN sélectionné'}
                          </span>
                        ) : (
                          <span className="text-gray-400">Scan Direct (Sans VPN/Agent)</span>
                        )}
                      </div>
                      {formData.agent_id && (
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Agent IP Interne</span>
                          <span className="text-white font-mono text-sm">
                            {availableAgents.find(a => a.id === formData.agent_id)?.internal_ip || '-'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* AI Configuration */}
                  <div className="pt-3 border-t border-gray-700">
                    <span className="text-xs text-gray-500 block mb-2 font-semibold">Configuration IA</span>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Modèle IA</span>
                        <span className="text-white">{formData.ai_model}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Mode Assistance IA</span>
                        <span className={formData.ai_assistance_mode === 'auto' ? 'text-brand-green' : 'text-blue-400'}>
                          {formData.ai_assistance_mode === 'auto' ? 'Automatique' : 'Manuel'}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Profondeur d'Analyse</span>
                        <span className="text-white capitalize">{formData.analysis_depth}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Analyse IA</span>
                        <span className={formData.ai_analysis ? 'text-brand-green' : 'text-gray-400'}>
                          {formData.ai_analysis ? 'Activée' : 'Désactivée'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Network Routing summary */}
                  {isTargetPublic && (
                    <div className="pt-3 border-t border-gray-700">
                      <span className="text-xs text-gray-500 block mb-2 font-semibold">Routage Réseau</span>
                      <div className="flex items-center gap-2">
                        {formData.network_mode === 'direct' && <><Globe className="w-4 h-4 text-gray-400" /><span className="text-white text-sm">Direct</span><span className="text-xs text-gray-500">— IP serveur sans tunnel</span></>}
                        {formData.network_mode === 'vpn' && <><Lock className="w-4 h-4 text-brand-green" /><span className="text-brand-green text-sm font-semibold">VPN</span>{formData.vpn_config_id && <span className="text-xs text-gray-400">— {vpnConfigs.externe?.find(v => v.id === formData.vpn_config_id)?.name || formData.vpn_config_id}</span>}</>}
                        {formData.network_mode === 'proxy' && <><Router className="w-4 h-4 text-blue-400" /><span className="text-blue-400 text-sm font-semibold">Proxy</span><span className="text-xs text-gray-400 font-mono ml-1">{formData.proxy_url || '(non configuré)'}</span></>}
                      </div>
                    </div>
                  )}

                  {/* Advanced Options */}
                  <div className="pt-3 border-t border-gray-700">
                    <span className="text-xs text-gray-500 block mb-2 font-semibold">Options Avancées</span>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Exploitation Auto</span>
                        <span className={formData.enable_exploitation ? 'text-yellow-500' : 'text-gray-400'}>
                          {formData.enable_exploitation ? 'Activée' : 'Désactivée'}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Recherche Dark Web</span>
                        <span className={formData.darkweb_search ? 'text-purple-400' : 'text-gray-400'}>
                          {formData.darkweb_search ? 'Activée' : 'Désactivée'}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Mode Furtif</span>
                        <span className={formData.stealth_mode !== 'none' ? 'text-brand-green' : 'text-gray-400'}>
                          {formData.stealth_mode === 'none' && 'Aucun'}
                          {formData.stealth_mode === 'basic' && '🔒 Basique'}
                          {formData.stealth_mode === 'advanced' && '🛡️ Avancé'}
                          {formData.stealth_mode === 'paranoid' && '👻 Paranoïaque'}
                        </span>
                      </div>
                      {formData.darkweb_search && (
                        <>
                          <div>
                            <span className="text-xs text-gray-500 block mb-1">Test Credentials Dark Web</span>
                            <span className={formData.test_darkweb_credentials ? 'text-purple-400' : 'text-gray-400'}>
                              {formData.test_darkweb_credentials ? 'Activé' : 'Désactivé'}
                            </span>
                          </div>
                          {formData.corporate_domain && (
                            <div>
                              <span className="text-xs text-gray-500 block mb-1">Domaine Corporatif</span>
                              <span className="text-purple-400 font-mono">{formData.corporate_domain}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Severity Filter */}
                  {formData.enable_exploitation && formData.severity_filter.length > 0 && (
                    <div className="pt-3 border-t border-gray-700">
                      <span className="text-xs text-gray-500 block mb-2">Filtre de Severite (Exploitation)</span>
                      <div className="flex gap-2 flex-wrap">
                        {formData.severity_filter.map(s => (
                          <span key={s} className={`px-2 py-1 text-xs rounded capitalize ${
                            s === 'critical' ? 'bg-red-500/20 text-red-400' :
                            s === 'high' ? 'bg-orange-500/20 text-orange-400' :
                            s === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Public IP Attacks */}
                  {isTargetPublic && formData.public_ip_attacks.enabled && (
                    <div className="pt-3 border-t border-gray-700">
                      <span className="text-xs text-gray-500 block mb-2 font-semibold">Attaques IP Publique / Box / Firewall</span>
                      <div className="flex gap-2 flex-wrap">
                        {formData.public_ip_attacks.box_firewall && (
                          <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded">Box/Firewall/Router</span>
                        )}
                        {formData.public_ip_attacks.vpn_server && (
                          <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">Serveur VPN</span>
                        )}
                        {formData.public_ip_attacks.default_creds && (
                          <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">Identifiants par défaut</span>
                        )}
                        {formData.public_ip_attacks.firmware_vuln && (
                          <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded">Vuln Firmware</span>
                        )}
                        {formData.public_ip_attacks.management_interface && (
                          <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded">Interface Admin</span>
                        )}
                        {!formData.public_ip_attacks.box_firewall && !formData.public_ip_attacks.vpn_server &&
                         !formData.public_ip_attacks.default_creds && !formData.public_ip_attacks.firmware_vuln &&
                         !formData.public_ip_attacks.management_interface && (
                          <span className="text-gray-500 italic text-xs">Aucune option selectionnee</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Internal Network Attacks */}
                  {!isTargetPublic && formData.target && !isDomain(formData.target) && formData.internal_network_attacks.enabled && (
                    <div className="pt-3 border-t border-gray-700">
                      <span className="text-xs text-gray-500 block mb-2 font-semibold">Attaques Réseau Interne</span>
                      <div className="flex gap-2 flex-wrap">
                        {formData.internal_network_attacks.ad_enumeration && (
                          <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">Active Directory</span>
                        )}
                        {formData.internal_network_attacks.smb_enumeration && (
                          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded">SMB/Partages</span>
                        )}
                        {formData.internal_network_attacks.service_discovery && (
                          <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">Services Internes</span>
                        )}
                        {formData.internal_network_attacks.kerberos_attacks && (
                          <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">Kerberos</span>
                        )}
                        {formData.internal_network_attacks.lateral_movement && (
                          <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded">Mouvement Lateral</span>
                        )}
                        {formData.internal_network_attacks.credential_spraying && (
                          <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded">Password Spraying</span>
                        )}
                        {formData.internal_network_attacks.network_poisoning && (
                          <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">Poisoning</span>
                        )}
                        {formData.internal_network_attacks.snmp_enumeration && (
                          <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded">SNMP</span>
                        )}
                        {!formData.internal_network_attacks.ad_enumeration && !formData.internal_network_attacks.smb_enumeration &&
                         !formData.internal_network_attacks.service_discovery && !formData.internal_network_attacks.kerberos_attacks &&
                         !formData.internal_network_attacks.lateral_movement && !formData.internal_network_attacks.credential_spraying &&
                         !formData.internal_network_attacks.network_poisoning && !formData.internal_network_attacks.snmp_enumeration && (
                          <span className="text-gray-500 italic text-xs">Aucune option selectionnee</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Subdomains */}
                  {selectedSubdomains.length > 0 && (
                    <div className="pt-3 border-t border-gray-700">
                      <span className="text-xs text-gray-500 block mb-2 font-semibold">Sous-domaines Sélectionnés</span>
                      <div className="flex gap-2 flex-wrap">
                        {selectedSubdomains.slice(0, 8).map((sub, idx) => (
                          <span key={idx} className="px-2 py-1 bg-brand-green/20 text-brand-green text-xs rounded">
                            {sub.subdomain}
                          </span>
                        ))}
                        {selectedSubdomains.length > 8 && (
                          <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded">
                            +{selectedSubdomains.length - 8} autres
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="text-blue-500 mt-0.5">ℹ️</div>
                  <div className="flex-1 text-xs text-gray-400">
                    <div className="font-medium text-blue-400 mb-1">Duree Estimee</div>
                    <div>
                      {formData.scan_type === 'network_discovery' && 'Environ 5-15 minutes'}
                      {formData.scan_type === 'vulnerability_scan' && 'Environ 30-60 minutes'}
                      {formData.scan_type === 'full_autonomous' && 'Environ 1-2 heures'}
                      {formData.darkweb_search && ' (+10-15 min pour Dark Web OSINT)'}
                      {isTargetPublic && formData.public_ip_attacks.enabled && ' (+15-30 min pour attaques Box/Firewall)'}
                      {!isTargetPublic && formData.internal_network_attacks.enabled && ' (+20-45 min pour attaques Réseau Interne)'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-brand-green/20">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className="px-6 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Retour
            </button>

            {currentStep < steps.length ? (
              <button
                onClick={handleNext}
                disabled={currentStep === 1 && !formData.target}
                className="btn-green disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Suivant
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="btn-green flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Lancer le scan IA
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default AIScanWizard;
